// BUILD-046 increment 1b-ii: what makes an active "Assigned to" filter obvious while the Filter popover is closed.
//
//   AssigneeFilterChip       a removable chip inline in the toolbar's wrapping row, beside the Customer / Job chip: "Assigned to: Eddie", with a Remove button named "Remove Assigned to filter". There
//                            is no chip without a filter. Removing it clears ONLY this view's Assigned to filter (assigneeFilterModel.removeAssigneeFilter; the status, department, Customer / Job,
//                            search, sort and density are not touched). Focus moves to the Filter button first, so a keyboard user is never dropped onto the page, and a polite line says the
//                            filter was removed. The chip's title is the whole name and the match rule.
//   AssigneeFilterEmptyState the truthful empty state for a chosen person that leaves no rows: it names the filter ("No tasks match Assigned to: Eddie with the other filters."), says which other
//                            filters also apply and offers to clear this one. It is never the generic "no tasks".
// The wording is in assigneeFilterModel.ts (describeAssigneeFilterChip, describeAssigneeFilterEmpty); this file draws it.
import { useEffect, useState } from 'react';
import { describeAssigneeFilterChip } from './assigneeFilterModel';
import type { AssigneeFilter, AssigneeFilterEmptyView } from './assigneeFilterModel';
import { focusToolbar } from './JobFilterChip';

export interface AssigneeFilterChipProps {
  filter: AssigneeFilter;
  onRemove: () => void;
}

export function AssigneeFilterChip({ filter, onRemove }: AssigneeFilterChipProps) {
  const chip = describeAssigneeFilterChip(filter);
  const [removed, setRemoved] = useState(false);
  const active = chip !== null;
  useEffect(() => {
    if (active) setRemoved(false);
  }, [active]);
  return (
    // INLINE in the toolbar's wrapping row (display: contents), exactly like the Customer / Job chip.
    <div className="contents" data-testid="assignee-filter-chip-area">
      {/* Always on the page, so the removal is announced after the chip itself is gone. */}
      <div role="status" aria-live="polite" className="sr-only">
        {!active && removed ? 'Assigned to filter removed. Showing all people.' : ''}
      </div>
      {chip !== null && (
        <span className="inline-flex max-w-full items-center gap-1 rounded-full border border-sky-200 bg-sky-50 py-0.5 pl-3 pr-1 text-xs text-gray-900" title={chip.title} data-testid="assignee-filter-chip">
          <span className="min-w-0 break-words">{chip.text}</span>
          <button
            type="button"
            aria-label={chip.removeLabel}
            title={chip.removeLabel}
            onClick={() => {
              focusToolbar();
              setRemoved(true);
              onRemove();
            }}
            className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full text-gray-700 hover:bg-sky-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2a7fb8]"
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </span>
      )}
    </div>
  );
}

export interface AssigneeFilterEmptyStateProps {
  view: AssigneeFilterEmptyView;
  onClear: () => void;
}

export function AssigneeFilterEmptyState({ view, onClear }: AssigneeFilterEmptyStateProps) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-6 text-center text-sm text-gray-600" data-testid="assignee-filter-empty">
      <p role="status" className="font-medium text-gray-800">
        {view.headline}
      </p>
      <p className="mt-1">{view.detail}</p>
      <button
        type="button"
        onClick={() => {
          focusToolbar();
          onClear();
        }}
        className="mt-3 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-[#1f6a99] hover:bg-sky-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2a7fb8]"
      >
        {view.clearLabel}
      </button>
    </div>
  );
}
